import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CrudService } from '../../core/services/crud.service';
import type { ListQuery } from '../../core/types/api';

interface TreeEntityMultiSelectProps<T extends { id: number; name?: string }> {
  cacheKey: readonly unknown[];
  service: CrudService<T, Partial<T>, Partial<T>>;
  query?: ListQuery;
  value: T[];
  onChange: (next: T[]) => void;
  label: string;
  helperText?: string;
  placeholder?: string;
  /**
   * Name of the field on T pointing to the parent id. For Brand it's
   * `'parentBrandId'`; for Category `'commonCategoryId'`, etc.
   */
  parentIdField: keyof T;
  /** Optional override for the option label (defaults to `entity.name`). */
  getLabel?: (entity: T) => string;
  /** Render small for use as a chip-style filter. Defaults to `'medium'`. */
  size?: 'small' | 'medium';
  /** How many chips to show in the input before collapsing. */
  limitTags?: number;
}

interface FlatNode<T> {
  entity: T;
  depth: number;
}

function buildTree<T extends { id: number }>(
  rows: T[],
  parentIdField: keyof T,
  getLabel: (t: T) => string,
): FlatNode<T>[] {
  const byParent = new Map<number | null, T[]>();
  for (const row of rows) {
    const parentId = (row[parentIdField] as number | null | undefined) ?? null;
    const bucket = byParent.get(parentId);
    if (bucket) bucket.push(row);
    else byParent.set(parentId, [row]);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => getLabel(a).localeCompare(getLabel(b)));
  }

  // Surface orphans (rows whose parent isn't loaded) as roots so they
  // don't silently disappear past the page-size cap.
  const knownIds = new Set(rows.map((r) => r.id));
  const orphanRoots = rows
    .filter((r) => {
      const pid = r[parentIdField] as number | null | undefined;
      return pid != null && !knownIds.has(pid);
    })
    .sort((a, b) => getLabel(a).localeCompare(getLabel(b)));

  const result: FlatNode<T>[] = [];
  const visit = (row: T, depth: number) => {
    result.push({ entity: row, depth });
    for (const child of byParent.get(row.id) ?? []) visit(child, depth + 1);
  };
  for (const root of byParent.get(null) ?? []) visit(root, 0);
  for (const orphan of orphanRoots) visit(orphan, 0);
  return result;
}

/**
 * Multi-select Autocomplete that renders its options as an indented
 * tree (DFS pre-order) for self-referencing entities like Brand and
 * Category. Each option has a leading checkbox so the user can see at
 * a glance which rows are already selected.
 *
 * Sibling of `TreeEntitySelect` (single-select); kept separate because
 * Material UI's `Autocomplete<T, true>` type signature differs from
 * `Autocomplete<T, false>` and a unified component bloats the surface.
 */
export function TreeEntityMultiSelect<T extends { id: number; name?: string }>({
  cacheKey,
  service,
  query,
  value,
  onChange,
  label,
  helperText,
  placeholder,
  parentIdField,
  getLabel,
  size = 'medium',
  limitTags = 1,
}: TreeEntityMultiSelectProps<T>) {
  const list = useQuery({
    queryKey: cacheKey,
    queryFn: () => service.list({ pageSize: 200, sort: 'name', ...query }),
  });

  const labelFor = useMemo(
    () => getLabel ?? ((o: T) => o.name ?? `#${o.id}`),
    [getLabel],
  );

  const flat = useMemo(
    () => buildTree(list.data?.data ?? [], parentIdField, labelFor),
    [list.data, parentIdField, labelFor],
  );

  // Selection preserves the node identity in `flat` so getOptionLabel
  // and isOptionEqualToValue line up cleanly.
  const selectedNodes = flat.filter((f) => value.some((v) => v.id === f.entity.id));

  return (
    <Autocomplete<FlatNode<T>, true, false, false>
      multiple
      disableCloseOnSelect
      fullWidth
      size={size}
      limitTags={limitTags}
      options={flat}
      value={selectedNodes}
      onChange={(_, next) => onChange(next.map((n) => n.entity))}
      getOptionLabel={(o) => labelFor(o.entity)}
      isOptionEqualToValue={(a, b) => a.entity.id === b.entity.id}
      loading={list.isLoading}
      renderOption={(props, option, { selected }) => (
        <Box
          component="li"
          {...props}
          key={option.entity.id}
          sx={{ pl: 1 + option.depth * 2 }}
        >
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            checked={selected}
            sx={{ mr: 1, p: 0.5 }}
          />
          {option.depth > 0 && (
            <Typography component="span" sx={{ color: 'text.disabled', mr: 1 }}>
              {'└─'}
            </Typography>
          )}
          <Typography variant="body2">{labelFor(option.entity)}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
